import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AllInboxOutlined,
  Extension,
  FolderShared,
  Hub,
  LockOutlined,
  OpenInNew,
  SmartToy,
} from '@mui/icons-material';
import resourceSharingService from '../../services/resourceSharingService';
import { usePermissions } from '../../context/PermissionsContext';
import { SHARING_COPY } from '../../constants/partnerCopy';
import { describeApiError } from '../../utils/apiError';
import styles from './SharedWithMe.module.scss';

const typeConfig = {
  agent: { label: 'Agents', icon: SmartToy },
  folder_dim1: { label: 'Rules book (D1)', icon: FolderShared },
  folder_dim3: { label: 'Customer Data (D3)', icon: FolderShared },
  composio_app: { label: 'Integrations', icon: Extension },
  custom_tool: { label: 'Custom tools', icon: Extension },
};

const tabs = [
  { value: 'all', label: 'All', icon: AllInboxOutlined },
  ...Object.entries(typeConfig).map(([value, config]) => ({ value, ...config })),
];

const getName = (item) => (
  item?.resource?.name
  || item?.resource?.app_name
  || item?.resource?.tool_key
  || `Resource ${item?.resource_id}`
);

const openPath = (item) => {
  switch (item.resource_type) {
    case 'agent':
      // `via=partner` lets the playground explain key errors: a partner-shared
      // agent runs on the runner's own company keys (Partner tier guide §8.3).
      return `/agent-test?id=${encodeURIComponent(item.resource_id)}${item.via === 'partner' ? '&via=partner' : ''}`;
    case 'folder_dim1':
      return `/knowledge-base/folder/${encodeURIComponent(item.resource_id)}?shared=true&permission=${item.permission}`;
    case 'folder_dim3':
      return `/knowledge-base-d3/folder/${encodeURIComponent(item.resource_id)}?shared=true&permission=${item.permission}`;
    case 'composio_app':
      return null;
    case 'custom_tool':
      return '/configure/tools?shared=true';
    default:
      return null;
  }
};

const SharedWithMe = () => {
  const navigate = useNavigate();
  const { partner } = usePermissions();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('all');
  const [previewItem, setPreviewItem] = useState(null);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const result = await resourceSharingService.getSharedWithMe();
        if (!cancelled) setItems(Array.isArray(result?.items) ? result.items : []);
      } catch (err) {
        if (!cancelled) {
          setError(describeApiError(err, 'Could not load shared resources.'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, []);

  const visible = useMemo(
    () => (tab === 'all' ? items : items.filter((item) => item.resource_type === tab)),
    [items, tab],
  );

  const counts = useMemo(() => items.reduce((result, item) => {
    result[item.resource_type] = (result[item.resource_type] || 0) + 1;
    return result;
  }, { all: items.length }), [items]);

  const selectedLabel = tabs.find((item) => item.value === tab)?.label?.toLowerCase() || 'resources';

  return (
    <main className={styles.page}>
      <div className={styles.inner}>
        <header className={styles.header}>
          <div className={styles.headerTop}>
            <div className={styles.titleBlock}>
              <div className={styles.eyebrow}>
                <FolderShared fontSize="inherit" />
                Team library
              </div>
              <h1 className={styles.title}>Shared with me</h1>
              <p className={styles.subtitle}>
                {partner?.name
                  ? `Resources your company, roles, teammates and ${partner.name} have made available to you.`
                  : 'Resources your company, roles, and teammates have made available to you.'}
              </p>
            </div>

            {!loading && (
              <div className={styles.countPill}>
                <span className={styles.countDot} aria-hidden="true" />
                {items.length} shared resource{items.length === 1 ? '' : 's'}
              </div>
            )}
          </div>

          <nav className={styles.tabs} aria-label="Shared resource types">
            {tabs.map(({ value, label, icon: Icon }) => (
              <button
                type="button"
                key={value}
                className={`${styles.tab} ${tab === value ? styles.tabActive : ''}`}
                aria-pressed={tab === value}
                onClick={() => setTab(value)}
              >
                <Icon fontSize="small" />
                <span>{label}</span>
                <span className={styles.tabCount}>{counts[value] || 0}</span>
              </button>
            ))}
          </nav>
        </header>

        <section className={styles.content} aria-live="polite">
          {loading ? (
            <div className={styles.statusPanel}>
              <CircularProgress size={34} />
            </div>
          ) : error ? (
            <div className={styles.statusPanel}>
              <Alert className={styles.error} severity="error">{error}</Alert>
            </div>
          ) : visible.length === 0 ? (
            <div className={styles.emptyState}>
              <div className={styles.emptyIcon}>
                <FolderShared sx={{ fontSize: 36 }} />
              </div>
              <p className={styles.emptyBadge}>Shared workspace</p>
              <h2 className={styles.emptyTitle}>No shared {selectedLabel} yet</h2>
              <p className={styles.emptyCopy}>
                When a teammate shares a resource with you, it will appear here automatically with the access level they granted.
              </p>
              <div className={styles.emptyTypes} aria-hidden="true">
                <span className={styles.emptyType}><SmartToy fontSize="small" /> Agents</span>
                <span className={styles.emptyType}><FolderShared fontSize="small" /> Knowledge</span>
                <span className={styles.emptyType}><Extension fontSize="small" /> Tools</span>
              </div>
            </div>
          ) : (
            <div className={styles.grid}>
              {visible.map((item) => {
                const config = typeConfig[item.resource_type] || typeConfig.custom_tool;
                const Icon = config.icon;
                const owner = item.owner?.name || item.owner?.email || 'A teammate';
                const ownerInitial = owner.trim().charAt(0) || 'T';
                const path = openPath(item);

                return (
                  <article className={styles.card} key={`${item.resource_type}-${item.resource_id}-${item.share_id}`}>
                    <div className={styles.cardTop}>
                      <div className={styles.cardIcon}><Icon /></div>
                      <div className={styles.badgeRow}>
                        {item.via === 'partner' && (
                          <Tooltip title={SHARING_COPY.sharedWithMeTooltip} arrow>
                            <span className={`${styles.permission} ${styles.viaPartner}`}>
                              <Hub sx={{ fontSize: 13 }} />
                              {SHARING_COPY.sharedWithMeBadge(partner?.name)}
                            </span>
                          </Tooltip>
                        )}
                        <span className={`${styles.permission} ${item.permission === 'write' ? styles.permissionWrite : ''}`}>
                          {item.permission === 'read' && <LockOutlined sx={{ fontSize: 13 }} />}
                          {item.permission === 'write' ? 'Can edit' : 'Read only'}
                        </span>
                      </div>
                    </div>

                    <div className={styles.cardBody}>
                      <h2 className={styles.cardTitle} title={getName(item)}>{getName(item)}</h2>
                      <div className={styles.owner}>
                        <span className={styles.ownerAvatar}>{ownerInitial}</span>
                        <span className={styles.ownerName}>Shared by {owner}</span>
                      </div>
                      <p className={styles.description}>
                        {item.resource?.description ||
                          (item.via === 'partner'
                            ? `${config.label.slice(0, -1)} shared by ${partner?.name || 'your partner'}.`
                            : `${config.label.slice(0, -1)} shared from your company.`)}
                      </p>
                    </div>

                    <div className={styles.cardFooter}>
                      <span className={styles.typeLabel}>{config.label.replace(/s$/, '')}</span>
                      <button
                        className={styles.openButton}
                        type="button"
                        onClick={() => (path ? navigate(path) : setPreviewItem(item))}
                      >
                        Open <OpenInNew sx={{ fontSize: 15 }} />
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </section>

        <Dialog open={Boolean(previewItem)} onClose={() => setPreviewItem(null)} maxWidth="sm" fullWidth>
          <DialogTitle>Shared integration</DialogTitle>
          <DialogContent dividers>
            <Typography variant="h6" sx={{ fontWeight: 750 }}>
              {previewItem ? getName(previewItem) : ''}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Shared by {previewItem?.owner?.name || previewItem?.owner?.email || 'a teammate'}
            </Typography>
            <div className={styles.dialogDetails}>
              <Typography variant="body2"><strong>Toolkit:</strong> {previewItem?.resource?.toolkit_slug || '—'}</Typography>
              <Typography variant="body2"><strong>Status:</strong> {previewItem?.resource?.status || '—'}</Typography>
              <Typography variant="body2">
                <strong>Authentication:</strong>{' '}
                {previewItem?.options?.auth_mode === 'without_auth' ? 'Connect your own account' : "Uses the owner's connection"}
              </Typography>
            </div>
          </DialogContent>
          <DialogActions><Button onClick={() => setPreviewItem(null)}>Close</Button></DialogActions>
        </Dialog>
      </div>
    </main>
  );
};

export default SharedWithMe;
